import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getSiteConfig } from '../services/api';

export default function Contact() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', message: '' });

  useEffect(() => {
    getSiteConfig().then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const phone = config?.phone || '+33647474068';
  const whatsapp = config?.whatsapp || '33647474068';
  const whatsappClean = whatsapp.replace(/\D/g, '');
  const address = config?.address || 'France';

  const buildWhatsappUrl = () => {
    const text = form.message
      ? `Bonjour Royal Cars,\n\nJe suis ${form.name || 'un client'}${form.phone ? ` (${form.phone})` : ''}.\n\n${form.message}`
      : `Bonjour Royal Cars, je souhaite vous contacter.`;
    return `https://wa.me/${whatsappClean}?text=${encodeURIComponent(text)}`;
  };

  return (
    <div className="bg-white min-h-screen">
      <Header />

      {/* Hero */}
      <div className="relative overflow-hidden text-white" style={{ background: 'linear-gradient(135deg, #111111 0%, #1c1c1c 60%, #2a2200 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 right-0 w-96 h-96 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, #eab308, transparent 70%)' }} />
          <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #eab308, transparent 70%)' }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-6 pb-16 flex flex-col lg:flex-row items-center gap-12">
          {/* Texte */}
          <div className="flex-1 text-center lg:text-left">
            <p className="text-yellow-500 text-sm font-bold uppercase tracking-widest mb-3">Nous contacter</p>
            <h1 className="text-5xl md:text-6xl font-extrabold mb-4 leading-tight">
              Parlons de votre <span className="text-yellow-500">projet</span>
            </h1>
            <p className="text-gray-300 text-xl max-w-xl leading-relaxed">
              Notre équipe est disponible pour répondre à toutes vos questions et vous accompagner dans votre achat.
            </p>
          </div>
          {/* Image */}
          <div className="flex-shrink-0 w-72 lg:w-80 rounded-3xl overflow-hidden shadow-2xl" style={{ border: '2px solid rgba(234,179,8,0.3)' }}>
            <img src="/imageia1.png" alt="Royal Cars famille heureuse" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>

      {/* Cartes de contact */}
      <div className="max-w-5xl mx-auto px-6 -mt-10 relative z-10 mb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Téléphone */}
          <a href={`tel:${phone}`}
            className="group bg-white rounded-2xl border border-gray-200 shadow-lg hover:shadow-2xl hover:border-yellow-500 transition-all duration-300 p-8 flex flex-col items-center text-center cursor-pointer hover:-translate-y-1">
            <div className="w-16 h-16 rounded-2xl bg-yellow-500 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-lg shadow-yellow-500/30">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
              </svg>
            </div>
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Appel direct</p>
            <h3 className="text-black font-extrabold text-xl mb-1">{phone}</h3>
            <p className="text-gray-500 text-sm">Lun–Sam · 9h–19h</p>
            <span className="mt-4 inline-flex items-center gap-1 text-yellow-600 font-bold text-sm group-hover:gap-2 transition-all">
              Appeler maintenant <span>→</span>
            </span>
          </a>

          {/* WhatsApp */}
          <a href={buildWhatsappUrl()} target="_blank" rel="noreferrer"
            className="group bg-white rounded-2xl border border-gray-200 shadow-lg hover:shadow-2xl hover:border-green-500 transition-all duration-300 p-8 flex flex-col items-center text-center cursor-pointer hover:-translate-y-1">
            <div className="w-16 h-16 rounded-2xl bg-green-500 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-lg shadow-green-500/30">
              <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">WhatsApp</p>
            <h3 className="text-black font-extrabold text-xl mb-1">Message rapide</h3>
            <p className="text-gray-500 text-sm">Réponse en quelques minutes</p>
            <span className="mt-4 inline-flex items-center gap-1 text-green-600 font-bold text-sm group-hover:gap-2 transition-all">
              Ouvrir WhatsApp <span>→</span>
            </span>
          </a>

          {/* Adresse */}
          <div className="group bg-white rounded-2xl border border-gray-200 shadow-lg hover:shadow-2xl hover:border-blue-400 transition-all duration-300 p-8 flex flex-col items-center text-center hover:-translate-y-1">
            <div className="w-16 h-16 rounded-2xl bg-blue-500 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-lg shadow-blue-500/30">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
            </div>
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Notre adresse</p>
            <h3 className="text-black font-extrabold text-xl mb-1">{address}</h3>
            <p className="text-gray-500 text-sm">Voir sur Maps</p>
            <span className="mt-4 inline-flex items-center gap-1 text-blue-600 font-bold text-sm group-hover:gap-2 transition-all">
              Obtenir l'itinéraire <span>→</span>
            </span>
          </div>
        </div>
      </div>

      {/* Google Maps — juste après les cartes */}
      <div className="max-w-5xl mx-auto px-6 pb-10">
        <div className="mb-6">
          <p className="text-yellow-500 text-sm font-bold uppercase tracking-widest mb-1">Localisation</p>
          <h2 className="text-2xl font-extrabold text-black">Retrouvez-nous ici</h2>
        </div>
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Carte */}
          <div className="flex-1 rounded-3xl overflow-hidden shadow-lg border border-gray-200" style={{ height: '360px' }}>
            <iframe
              title="Royal Cars localisation"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(config?.address || '8 Allée des Plombes 77185 Lognes')}&output=embed&z=16`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          {/* Infos adresse */}
          <div className="lg:w-72 flex flex-col justify-center gap-5">
            <div>
              <p className="text-yellow-500 text-xs font-bold uppercase tracking-widest mb-2">Notre adresse</p>
              <p className="text-black font-extrabold text-xl leading-snug">{config?.address || '8 Allée des Plombes, 77185 Lognes'}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-yellow-500 flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Téléphone</p>
                <p className="text-black font-bold">{config?.phone || '+33 6 47 47 40 68'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Horaires</p>
                <p className="text-black font-bold">Lun–Sam · 9h–19h</p>
              </div>
            </div>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(config?.address || '8 Allée des Plombes 77185 Lognes')}`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-black text-white py-3 px-6 rounded-xl font-bold hover:bg-gray-800 transition text-sm"
            >
              📍 Ouvrir dans Google Maps
            </a>
          </div>
        </div>
      </div>

      {/* Section formulaire WhatsApp + horaires */}
      <div className="max-w-5xl mx-auto px-6 pt-10 pb-4">
        <p className="text-yellow-500 text-sm font-bold uppercase tracking-widest mb-1">Contact</p>
        <h2 className="text-2xl font-extrabold text-black mb-8">Écrivez-nous ou consultez nos horaires</h2>
      </div>
      <div className="max-w-5xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

          {/* Formulaire → WhatsApp */}
          <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-yellow-500 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                </svg>
              </div>
              <div>
                <h2 className="font-extrabold text-black text-xl">Nous écrire</h2>
                <p className="text-gray-400 text-sm">Votre message sera envoyé via WhatsApp</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1.5 block">Votre prénom</label>
                <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Jean Dupont"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-yellow-500 transition text-sm" />
              </div>
              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1.5 block">Votre téléphone</label>
                <input type="tel" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+33 6 xx xx xx xx"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-yellow-500 transition text-sm" />
              </div>
              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1.5 block">Votre message</label>
                <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                  rows={4} placeholder="Bonjour, je suis intéressé par... J'aimerais avoir plus d'informations sur..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-yellow-500 transition text-sm resize-none" />
              </div>
              <a href={buildWhatsappUrl()} target="_blank" rel="noreferrer"
                className="flex items-center justify-center gap-3 w-full bg-green-500 text-white py-4 rounded-xl font-bold text-base hover:bg-green-600 transition shadow-lg shadow-green-500/20 hover:shadow-green-500/40">
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Envoyer via WhatsApp
              </a>
              <p className="text-gray-400 text-xs text-center">
                Votre message sera pré-rempli dans WhatsApp. Aucun compte requis.
              </p>
            </div>
          </div>

          {/* Infos pratiques */}
          <div className="flex flex-col gap-6">

            {/* Horaires */}
            <div className="bg-black text-white rounded-3xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-yellow-500 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <h2 className="font-extrabold text-white text-xl">Horaires d'ouverture</h2>
              </div>
              <div className="space-y-3">
                {[
                  { jour: 'Lundi — Vendredi', horaire: '9h00 — 19h00', open: true },
                  { jour: 'Samedi', horaire: '9h00 — 17h00', open: true },
                  { jour: 'Dimanche', horaire: 'Fermé', open: false },
                ].map(({ jour, horaire, open }) => (
                  <div key={jour} className="flex items-center justify-between py-2 border-b border-white/10 last:border-0">
                    <span className="text-gray-400 text-sm">{jour}</span>
                    <span className={`font-bold text-sm ${open ? 'text-yellow-500' : 'text-gray-600'}`}>{horaire}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-green-400 text-sm font-semibold">WhatsApp disponible 7j/7</span>
              </div>
            </div>

            {/* Engagement */}
            <div className="bg-yellow-500 rounded-3xl p-8">
              <h2 className="font-extrabold text-black text-xl mb-4">Notre engagement</h2>
              <div className="space-y-3">
                {[
                  'Réponse garantie sous 2h en journée',
                  'Conseil personnalisé sans engagement',
                  'Transparence sur l\'état de chaque véhicule',
                  'Livraison possible sur toute la France, l\'Algérie et la Tunisie',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-black flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                    <span className="text-black font-semibold text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
